// Types for the onboarding analytics dashboard (CWA-159, spec 009).
//
// The API returns snake_case; everything here is the camelCase domain shape the
// service module maps onto at the boundary, so no component ever sees a wire
// key. See specs/009-onboarding-analytics-dashboard/data-model.md.
//
// Every `state` is a plain `string`, NOT OnboardingState. The API will normally
// send a known identifier, but a backend newer than this build can send one we
// have no label for, and that must survive as far as stateLabel() rather than
// failing a type narrowing here (FR-032).

import {
  CustomerType,
  LeadSource,
  ProspectTypeFilter,
  SetupFeeStatus,
  SignMethod,
} from "./onboarding";

// ---- Section 1: KPI summary ----
// Nine figures describing the pipeline right now. Deliberately carries no
// prior-period value: the API returns none, so no delta can be shown (FR-005).
export interface DashboardKpis {
  totalInPipeline: number;
  totalCustomersLive: number;
  newProspectsThisWeek: number;
  newProspectsThisMonth: number;
  newCustomersThisWeek: number;
  newCustomersThisMonth: number;
  // Both of these come back as 0.0 for an empty database AND for a genuine
  // zero. hasMeasurement() in utils/analyticsFormat decides which it is.
  overallConversionRatePct: number;
  avgOnboardingDurationDays: number;
  overdueCount: number;
}

// ---- Section 2: Pipeline funnel ----
// `count` is CUMULATIVE — prospects at or past this stage — so across stages in
// pipeline order it can only decrease. isFunnelMonotonic() enforces that before
// anything is drawn (FR-019).
//
// The API's `label_de` is deliberately NOT carried here. A German label that
// must never be rendered is a trap; it is dropped at the boundary (FR-030).
export interface FunnelStage {
  state: string;
  count: number;
  dropOff: number | null; // null on the first stage
  dropOffPct: number | null; // also null when the previous count was zero
}

// ---- Section 3a: Time per step ----
// The API sends one string, "FROM → TO". It is split at the boundary so no
// component has to know the separator (FR-031).
export interface StepDuration {
  from: string;
  to: string;
  avgDays: number;
}

// ---- Section 3b: Duration trend ----
export interface MonthlyDuration {
  month: string; // "YYYY-MM"
  avgDays: number; // meaningless when count is 0
  count: number;
}

// ---- Section 3c: Sign method breakdown ----
export interface SignMethodStat {
  method: SignMethod | string;
  avgDays: number;
  count: number;
}

// ---- Section 4: Prospect list ----
export interface ProspectAnalyticsRow {
  id: string;
  fullName: string;
  customerType: CustomerType | string;
  state: string;
  leadSource: LeadSource | string | null;
  vendor: string;
  signMethod: SignMethod | string | null;
  createdAt: string;
  lastChangedAt: string | null;
  daysInState: number;
  daysTotal: number;
  lastActor: string | null;
  // CWA-196. Null from a backend that predates it.
  setupFeeStatus: SetupFeeStatus | null;
}

export interface ProspectAnalyticsPage {
  total: number;
  page: number;
  size: number;
  prospects: ProspectAnalyticsRow[];
}

// Query for GET /list. Every field maps to a parameter the API validates, so an
// unsupported sort column or an out-of-range size is a 400 rather than a
// silently ignored hint — see contracts/dashboard-api.md.
export type ProspectSortColumn =
  | "name"
  | "customer_type"
  | "state"
  | "lead_source"
  | "vendor"
  | "sign_method"
  | "created_at"
  | "last_changed_at"
  | "days_in_state"
  | "days_total"
  | "last_actor";

// The whitelist the API enforces. Sorting by anything else is refused, so the
// table must not offer a column outside this list (FR-013).
export const PROSPECT_SORT_COLUMNS: ProspectSortColumn[] = [
  "name",
  "customer_type",
  "state",
  "lead_source",
  "vendor",
  "sign_method",
  "created_at",
  "last_changed_at",
  "days_in_state",
  "days_total",
  "last_actor",
];

export const MAX_PROSPECT_PAGE_SIZE = 100;

export interface ProspectListQuery {
  page?: number;
  size?: number; // 1..100; outside that the API answers 400
  sort?: ProspectSortColumn;
  order?: "asc" | "desc";
  states?: string[];
  customerType?: CustomerType | null;
  signMethod?: SignMethod | null;
  leadSource?: string | null;
  vendor?: string | null;
  dateFrom?: string | null; // ISO-8601
  dateTo?: string | null;
  dateField?: "created" | "last_changed";
  setupFeeStatus?: SetupFeeStatus | null;
  // CWA-184: which onboardings to list; the backend lists new customers unless told otherwise.
  prospectType?: ProspectTypeFilter | null;
}

// ---- Section 5: Segmentation ----
export interface LeadSourceStat {
  source: string;
  count: number;
  percentage: number;
}

export interface CustomerTypeStats {
  count: number;
  avgDurationDays: number;
  conversionRatePct: number;
}

// The API sends an object with two fixed keys, not an array. Renamed here
// because `private` reads as a keyword at every call site.
export interface CustomerTypeSegmentation {
  privateCustomers: CustomerTypeStats;
  businessCustomers: CustomerTypeStats;
}

export interface VendorStat {
  vendor: string;
  count: number;
  avgSetupDays: number; // OCPP_CREDENTIALS_SENT → STATION_PUBLIC
}

// ---- Section 6: Operational alerts ----
// Overdue prospects only. The epic also described reminder and failed-
// notification alerts; neither is implemented, so the section must not imply
// the list is broader than it is (FR-009).
export interface OverdueAlert {
  prospectId: string;
  name: string;
  state: string;
  daysStuck: number;
}

// ---- Section 7: Volume ----
export interface MonthlyVolume {
  month: string; // "YYYY-MM"
  newProspects: number;
  completed: number;
}

// ---- Section 8: Satisfaction (CWA-207) ----
// The ratings given by prospects completed in the last N months. One cohort
// feeds every figure, so the response rate's denominator is the same set the
// average is taken over.
export type StarValue = 1 | 2 | 3 | 4 | 5;

export const STAR_VALUES: StarValue[] = [1, 2, 3, 4, 5];

export interface SatisfactionComment {
  prospectId: string;
  name: string;
  stars: number;
  comment: string;
  submittedAt: string;
}

export interface OnboardingSatisfaction {
  months: number;
  completedCount: number;
  ratedCount: number;
  averageStars: number | null; // null when nothing was rated
  responseRatePercent: number | null; // null when nothing was completed
  distribution: Record<StarValue, number>;
  comments: SatisfactionComment[]; // every comment of the period, newest first
}

// ---- Shared ----
// The months parameter accepted by /duration-trend and /volume. Outside this
// range the API answers 400, so no control may offer a value outside it.
export const MIN_TREND_MONTHS = 1;
export const MAX_TREND_MONTHS = 24;

// Which sections the dashboard fetches. Each one loads, fails and retries on its
// own (FR-034), so this is the key for every per-section piece of state.
export type AnalyticsSection =
  | "kpis"
  | "alerts"
  | "funnel"
  | "prospects"
  | "timePerStep"
  | "durationTrend"
  | "signMethod"
  | "leadSource"
  | "customerType"
  | "vendor"
  | "volume"
  | "satisfaction";

// A refusal we cannot retry out of (not an admin) reads differently from a
// transient one, and must not offer a retry that can never succeed (FR-035a).
export type AnalyticsErrorKind = "forbidden" | "invalid" | "transient";

export interface AnalyticsError {
  kind: AnalyticsErrorKind;
  message: string;
}
